import { resolveDropSlot, type SlotRow } from './resolveDropSlot'

export interface DropTarget {
  overId: string
  after: boolean
}

/**
 * Which row a drag resolves to, measured from the DRAGGED ROW's vertical
 * centre.
 *
 * Not from the pointer. The pointer sits wherever the row was grabbed — on the
 * grip at the far left, usually well above or below the row's middle — so
 * measuring from it meant the target only changed once the cursor itself
 * crossed a boundary, long after the floating row visibly covered it. The
 * indicator tracked the cursor while the eye tracked the row.
 *
 * Resolution order:
 *
 * 1. **A team row containing the centre**, as a slot — above its midpoint means
 *    "before it", below means "after it". This is what keeps the gap below a
 *    division's last team reachable.
 * 2. **A schedule row containing the centre**, meaning "drop INTO this
 *    division". The only target an EMPTY or collapsed division can offer, since
 *    it has no team rows of its own.
 * 3. **Past the last row entirely** — in the container's trailing padding — the
 *    last team row, appended. Nothing is below it to aim at.
 *
 * Teams are checked first: a team row nested inside a division's bounds would
 * otherwise lose to its own parent header on overlapping geometry.
 *
 * Returns null when the centre is above everything or the rail is empty, so a
 * drag that has not reached any row resolves to no target rather than guessing.
 *
 * `draggedId` is excluded from both row sets. Rows stay put while dragging, so
 * the lifted row's rect still sits in its original slot; left in, it shadows
 * whatever the centre has actually moved onto and the drag resolves back to
 * where it started.
 */
export const resolveDropTarget = (
  /** Division and conference header rows, in visual order. */
  scheduleRows: SlotRow[],
  /** Team rows, in visual order. */
  teamRows: SlotRow[],
  draggedCentreY: number | undefined,
  /** The row being dragged, which must not be its own target. */
  draggedId?: string | null
): DropTarget | null => {
  if (draggedCentreY === undefined) {
    return null
  }

  const notDragged = (row: SlotRow) => row.id !== draggedId
  const teams = draggedId ? teamRows.filter(notDragged) : teamRows
  const schedules = draggedId ? scheduleRows.filter(notDragged) : scheduleRows

  const slot = resolveDropSlot(teams, draggedCentreY)
  if (slot) {
    return slot
  }

  const schedule = schedules.find(
    (row) => draggedCentreY >= row.top && draggedCentreY < row.bottom
  )
  if (schedule) {
    return { overId: schedule.id, after: false }
  }

  // Below every row: the rail's trailing padding. Append to the last team.
  const lastTeam = teams[teams.length - 1]
  if (lastTeam && draggedCentreY >= lastTeam.bottom) {
    return { overId: lastTeam.id, after: true }
  }

  return null
}
